import { getCurrentInstance } from 'vue'

/**
 * Returns the Vuex store from inside `setup()` / `<script setup>`.
 *
 * Vuex 3 predates the Composition API and ships no `useStore` of its own, so we
 * read the store off the current instance the same way `this.$store` does in an
 * Options API component.
 *
 * Deliberately NOT `import store from '@state/store'`: the singleton import
 * would bypass the store that tests inject via `createComponentMocks({ store })`
 * (see tests/unit/setup.js), making every store-backed component untestable.
 *
 * @returns {import('vuex').Store} the store this component is mounted with
 */
export function useStore() {
    const instance = getCurrentInstance()

    if (!instance) {
        throw new Error('useStore() can only be called inside setup() or <script setup>.')
    }

    const store = instance.proxy.$store

    if (!store) {
        throw new Error(
            'useStore() found no Vuex store on this component. In tests, mount it with createComponentMocks({ store }).'
        )
    }

    return store
}
